// @flow

import React from 'react';
import ActionsColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/ActionsColumn';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ListPage from 'esm-web-pro/containers/pages/ListPage';
import { bindActionCreators } from 'redux';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { connect, useDispatch } from 'react-redux';
import withRouter from 'speed-js-react/src/router/withRouter';
import { APIContext } from 'speed-js-react';
import QuickViewIcon from 'esm-web-pro/components/icons/QuickViewIcon';
import ColumnSettingsFormComponent from 'esm-web-pro/containers/tables/ColumnSettingsFormComponent';
import EscalationMatrixSidePanel from
    'esm-web-pro/components/pages/nomenclatures/escalation-matrices/sidepanel/EscalationMatrixSidePanel';
import { Route } from 'react-router-dom';
import TaskTypeSidePanel from
    'esm-web-pro/components/pages/nomenclatures/sidepanel/EscalationMatricesTaskTypeSidePanel';

const ESCALATION_MATRIX_SIDEPANEL_KEY = 'ESCALATION_MATRIX_SIDEPANEL_KEY';
const TASK_TYPE_SIDEPANEL_KEY = 'TASK_TYPE_SIDE_PANEL_KEY';
type Props = {
    match: Object,
    addBreadcrumbs: (title: string, link: string) => void,
    removeBreadcrumb: (title: string) => void,
};

const messages = defineMessages({
    matrices: {
        id: 'adminpage-nomenclature-task-type-matrices',
        defaultMessage: 'Matrices',
    },
});

const EscalationMatricesTaskTypeDashboard = (props: Props) => {
    const listRef = React.useRef(null);

    const reloadData = () => {
        if (listRef.current && listRef.current.table.current) {
            listRef.current.table.current.reloadData();
        }
    };
    const {
        match: { params: { id: taskTypeId } },
        addBreadcrumbs: addBreadcrumbsAction, removeBreadcrumb: removeBreadcrumbAction,
    } = props;

    const { apiClient } = React.useContext(APIContext);
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();

    const [taskType, setTaskType] = React.useState(null);
    const [escalationMatrices, setEscalationMatrices] = React.useState(null);

    const reloadTaskType = React.useCallback(() => {
        apiClient.resources.nomenclatureTaskType.get(taskTypeId).then((response) => setTaskType(response));
        apiClient.resources.taskTypeEscalationMatrices.list(taskTypeId).then((response) => {
            setEscalationMatrices(response);
        });
    }, [apiClient, taskTypeId]);

    React.useEffect(() => {
        reloadTaskType();
    }, [reloadTaskType]);

    React.useEffect(() => {
        if (taskType && escalationMatrices) {
            dispatch(addBreadcrumbsAction([
                {
                    title: formatMessage(messages.matrices),
                    link: `/administration/nomenclatures/task-types/${taskType.id}`,
                },

            ]));
        }
        return () => {
            if (taskType) {
                dispatch(removeBreadcrumbAction(formatMessage(messages.matrices)));
            }
        };
    }, [addBreadcrumbsAction, removeBreadcrumbAction, dispatch, taskType, taskTypeId, escalationMatrices,
        formatMessage]);

    return (taskType && escalationMatrices) && (
        <>
            <Route path="/items/{id}/dashboard">
                <EscalationMatrixSidePanel
                    reload={reloadTaskType}
                    panelId={ESCALATION_MATRIX_SIDEPANEL_KEY}
                />
            </Route>

            <TaskTypeSidePanel
                panelId={TASK_TYPE_SIDEPANEL_KEY}
                object={taskType}
                resourceArguments={[escalationMatrices.id]}
                reload={reloadData}
            />
            <ListPage
                ref={listRef}
                {...props}
                resourceName="taskTypeEscalationMatrices"
                resourceArguments={[taskTypeId]}
                permissions={['nomenclatures.view_nomenclaturetype']}
                tableId="administration-nomenclatures-items-table"
                columnSettingsFormComponent={ColumnSettingsFormComponent}
                columns={[
                    {
                        Header: <FormattedMessage id="sequence" defaultMessage="Sequence" />,
                        accessor: 'sequence',
                        minWidth: 120,
                    },
                    {
                        Header: <FormattedMessage
                            id="organisational-unit-name"
                            defaultMessage="Organisational Unit Name"
                        />,
                        Cell: ({ original }) => original.org_unit?.map((ou) => ou.name).join(', '),
                        minWidth: 120,
                    },
                    {
                        actions: [
                            {
                                label: <FormattedMessage
                                    id="projmanag-counterparties-quickview"
                                    defaultMessage="Quick View"
                                />,
                                type: 'quickView',
                                accessor: 'uuid',
                                resourceArguments: [taskTypeId],
                                component: EscalationMatrixSidePanel,
                                resourceName: 'taskTypeEscalationMatrices',
                                icon: <QuickViewIcon />,
                                to: '/{id}/quick_view',
                                getTitle: (obj) => obj.org_unit.map((ou) => ou.name).join(', '),
                                panelId: ESCALATION_MATRIX_SIDEPANEL_KEY,
                                reload: reloadData,
                            }],
                        Cell: ActionsColumn,
                        minWidth: 70,
                    },
                ]}
                newObjectLabel={(
                    <FormattedMessage
                        id="escalation-matrix-new"
                        defaultMessage="New Escalation Matrix"
                    />
                )}
                newObjectFormProps={{
                    excludeFields: ['type'],
                    initial: { type: taskType.code },
                    nestedSchemaQuery: { create: true },
                }}
            />
        </>
    );
};

const mapDispatchToProps = ((dispatch) => (bindActionCreators({ addBreadcrumbs, removeBreadcrumb }, dispatch)));

export default withRouter(connect(null, mapDispatchToProps)(EscalationMatricesTaskTypeDashboard));
